import { useEffect } from 'react'
import { generatePath, useHistory, useLocation } from 'react-router-dom'
import { reportsCampaignsPath, unifiedFlowPaths } from 'app/navigation/paths'
import { useFlags } from 'launchdarkly-react-client-sdk'
import CampaignSummary from 'workflow/CampaignSummary/CampaignSummary'
import CampaignSummaryThoughspot from 'workflow/CampaignSummary/CampaignSummaryThoughspot'
import PersonalizationSummary from 'workflow/PersonalizationSummary/PersonalizationSummary'
import { fetchCampaignData } from 'workflow/Workflow.actions'

import Loader from 'common/components/loaders/Loader'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { isComponentElement } from 'features/unifiedFlow/api/interfaces'

import { CurrentUsersBanner } from '../../components/CurrentUsersBanner'
import CenterPanel from '../../components/layout/CenterPanel'
import PageDetails from '../../components/PageDetails'
import { getIsPersonalized } from '../../helpers'
import { useContent } from '../../hooks'

const CenterSection = () => {
  const history = useHistory()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const { shouldUseManageInsteadOfReview, showBothReviewAndManage } = useFlags()
  const isThoughtspot = location.pathname.includes('results-thoughtspot')

  const { content } = useContent()
  const selectedElementId = useAppSelector(
    (state) => state.unifiedFlow.selectedElementId
  )

  const elements = content?.elements ?? []
  const componentElements = elements?.filter(isComponentElement)

  const selectedComponentElement = componentElements?.find(
    (element) => element.element_id === selectedElementId
  )

  const currentCampaignId = selectedComponentElement?.campaign_id
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const isLoadingCampaignDetails = useAppSelector(
    (state) => state.campaignStates.isLoadingCampaignDetails
  )
  const isLoadingCampaign = useAppSelector(
    (state) => state.campaignStates.isLoading
  )
  const isPersonalised = getIsPersonalized(selectedComponentElement)
  const isLoading = currentCampaignId
    ? isLoadingCampaignDetails || isLoadingCampaign
    : isLoadingCampaignDetails

  useEffect(() => {
    const abortController = new AbortController()
    if (currentCampaignId) {
      dispatch(fetchCampaignData(currentCampaignId, {}, abortController.signal))
    }
    return () => {
      abortController.abort()
    }
  }, [dispatch, currentCampaignId])

  useEffect(() => {
    if (isThoughtspot && accountId) {
      const reportsUrl = generatePath(reportsCampaignsPath, { accountId })
      history.replace(reportsUrl)
    }
  }, [isThoughtspot, accountId, history])

  return (
    <CenterPanel className="flex flex-col overflow-auto">
      {isLoading ? (
        <Loader className="m-auto" />
      ) : (
        <>
          {!isPersonalised && (
            <>
              <CurrentUsersBanner />
              <PageDetails
                title="Results"
                description="View your results based on inputs and dynamic optimization"
                className="pb-8"
              />
              {isThoughtspot ? (
                <CampaignSummaryThoughspot
                  updatedCampaignId={currentCampaignId}
                  shouldFetchOnMount={false}
                  key={currentCampaignId}
                  onRedirect={() => {
                    const path = showBothReviewAndManage
                      ? unifiedFlowPaths.review
                      : shouldUseManageInsteadOfReview
                      ? unifiedFlowPaths.manage
                      : unifiedFlowPaths.review
                    history.push(
                      generatePath(path, {
                        contentId: content?._id,
                      })
                    )
                  }}
                />
              ) : (
                <CampaignSummary
                  updatedCampaignId={currentCampaignId}
                  key={currentCampaignId}
                  shouldFetchOnMount={false}
                  onRedirect={() => {
                    const path = showBothReviewAndManage
                      ? unifiedFlowPaths.review
                      : shouldUseManageInsteadOfReview
                      ? unifiedFlowPaths.manage
                      : unifiedFlowPaths.review
                    history.push(
                      generatePath(path, {
                        contentId: content?._id,
                      })
                    )
                  }}
                />
              )}
            </>
          )}
          {isPersonalised && (
            <>
              <PageDetails
                title="Results"
                description="View your results based on inputs"
                className="pb-8"
              />
              <PersonalizationSummary />
            </>
          )}
        </>
      )}
    </CenterPanel>
  )
}

export default CenterSection
